/**
 * TransactionCodeCard — Phase 4c+++++++ (Feb 2026)
 *
 * Kompakte Dashboard-Karte mit Transaktions-Code, Copy-Button und QR-Code
 * zum Versenden / Drucken. Wird zwischen „1 · Nachrichten" und
 * „Werkstatt-Aufträge" im Dealer-Dashboard angezeigt.
 *
 * Kommt aus der Marketing-Sektion zur direkten Sichtbarkeit im Workflow.
 */
import { useState } from "react";
import { Copy, QrCode, Download, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

export default function TransactionCodeCard({ user }) {
    const [qrOpen, setQrOpen] = useState(false);
    if (!user || user.role !== "b2b") return null;
    const code = user.customer_number ? `H_${user.customer_number}` : null;
    if (!code) return null;

    const portalBase = window.location.origin;
    const qrPayload = `${portalBase}/?ref=${encodeURIComponent(code)}`;

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success("Transaktions-Code kopiert");
        } catch {
            toast.error("Bitte Code manuell markieren");
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(qrPayload);
            toast.success("Link kopiert");
        } catch {
            toast.error("Bitte Link manuell markieren");
        }
    };

    const downloadQr = () => {
        const svg = document.querySelector('[data-testid="dashboard-tx-qr-svg"]');
        if (!svg) return;
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transaktionscode-${code}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="bg-[#111827] text-white rounded-sm shadow-sm overflow-hidden"
            data-testid="dashboard-tx-code-card"
        >
            <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">
                        Ihr Transaktions-Code
                    </div>
                    <div className="font-mono-num font-black text-base text-[#FFCC00] truncate" data-testid="dashboard-tx-code-value">
                        {code}
                    </div>
                </div>
                <button
                    onClick={copyCode}
                    className="inline-flex items-center gap-1 px-2 py-1.5 bg-white/10 hover:bg-white/20 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                    data-testid="dashboard-tx-copy"
                    title="Code kopieren"
                >
                    <Copy className="w-3 h-3" /> Code
                </button>
                <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-1 px-2 py-1.5 bg-white/10 hover:bg-white/20 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                    data-testid="dashboard-tx-copy-link"
                    title={`Link kopieren: ${qrPayload}`}
                >
                    <ExternalLink className="w-3 h-3" /> Link
                </button>
                <button
                    onClick={() => setQrOpen((v) => !v)}
                    className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                        qrOpen ? "bg-[#FFCC00] text-[#111827]" : "bg-white/10 hover:bg-white/20"
                    }`}
                    data-testid="dashboard-tx-qr-toggle"
                    title="QR-Code anzeigen"
                >
                    <QrCode className="w-3 h-3" /> QR
                </button>
            </div>
            {qrOpen && (
                <div className="border-t border-white/10 px-3 py-3 bg-white/5 flex items-start gap-4">
                    <div className="bg-white p-2 rounded-sm shrink-0">
                        <QRCodeSVG
                            value={qrPayload}
                            size={120}
                            bgColor="#FFFFFF"
                            fgColor="#111827"
                            level="M"
                            data-testid="dashboard-tx-qr-svg"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] leading-relaxed text-gray-300">
                            Scannen Sie diesen Code mit dem Smartphone oder drucken Sie ihn als
                            Aufkleber/Karte. Jeder Kauf-Abschluss über diesen Link wird Ihrem
                            Konto im Tarif-System gutgeschrieben.
                        </p>
                        <div className="font-mono-num text-[10px] text-gray-400 mt-1.5 truncate" title={qrPayload}>
                            {qrPayload}
                        </div>
                        <button
                            onClick={downloadQr}
                            className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm text-[10px] font-black uppercase tracking-wider"
                            data-testid="dashboard-tx-qr-download"
                        >
                            <Download className="w-3 h-3" /> SVG laden
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
